import type { MouseEvent, ReactElement } from "react"
import UnderlineLinkAtm from "@onestore/hel/dist/components/atoms/UnderlineLinkAtm"
import GridOrg from "@onestore/hel/dist/components/organisms/GridOrg"
import type Theme from "@onestore/hel/dist/typings/theme"
import { getGroupedTerms } from "@gatsby-plugin-checkout/helpers/getGroupedTerms"
import { useCheckoutConfigurationQuery } from "@gatsby-plugin-checkout/hooks/useCheckoutConfigurationQuery"
import ModalTemplateWrapper from "~/components/ModalTemplateWrapper"
import type { ModalTermsLink } from "~/fragments/modalTermsLink"
import useModalState from "~/hooks/useModalState"
import { getSummaryTerms } from "~/store/checkout-terms/selectors"
import { useAppSelector } from "~/store/hooks"
import ModalExpendableAgreement from "../ModalExpendableAgreement"

type ModalWithAgreementsLinkProps = ModalTermsLink["flatData"] & {
  typography?: Theme.Typography
  emphasis?: Theme.Emphasis
}

const ModalWithAgreementsLink = ({
  text,
  title,
  modalTitle,
  typography,
  emphasis,
}: ModalWithAgreementsLinkProps): ReactElement<ModalWithAgreementsLinkProps> => {
  const { isOpen, open, close } = useModalState()

  const { expandedTerms, groupedTermsHiddenIds } =
    useCheckoutConfigurationQuery()
  const expandedTermsList: number[] = expandedTerms.map((term) => term.id)

  const terms = useAppSelector(getSummaryTerms)
  const { groupedTermsWithoutHiddens } = getGroupedTerms(
    terms,
    groupedTermsHiddenIds
  )

  const handleOnClickLink = (event: MouseEvent) => {
    event.preventDefault()

    open()
  }

  const termsModalContentElement = (
    <GridOrg columns={1} gutterSpace={{ large: 4, medium: 2, small: 2 }}>
      {groupedTermsWithoutHiddens.map((term) => (
        <ModalExpendableAgreement
          key={`${term.type}-${term.id}`}
          name={term.name}
          text={term.text}
          isExpanded={expandedTermsList.includes(term.id)}
        />
      ))}
    </GridOrg>
  )

  return (
    <>
      <UnderlineLinkAtm
        typography={typography || "medium1_h"}
        title={title}
        onClick={handleOnClickLink}
        emphasis={emphasis}
      >
        {text}
      </UnderlineLinkAtm>

      <ModalTemplateWrapper
        title={modalTitle}
        typography="large3"
        isOpen={isOpen}
        onCloseButtonClick={close}
        content={termsModalContentElement}
        isHiddenUnderline
      />
    </>
  )
}

export default ModalWithAgreementsLink
